import {
  formatDayShort,
  formatDuration,
  formatMoney,
  formatNumber,
  formatTime,
  plural,
} from '@trips/shared/format';
import { ITEM_TYPE_LABELS, rentalDays, spanNights } from '@trips/shared/items';
import type { Item, Place } from '@trips/shared/schema';
import { localDate, tzAbbr } from '@trips/shared/time';
import { type ReactNode, useState } from 'react';
import { patch, remove } from '../../data/mutate';
import {
  selectAttachmentsByOwner,
  selectPlaceLookup,
  selectPrefs,
  selectTripLists,
} from '../../data/selectors';
import { useStore } from '../../data/store';
import { widenTripFor } from '../../data/trips';
import { copyText, haptics } from '../../lib/platform';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { Icon, type IconName } from '../../ui/Icon';
import { ListGroup, ListRow } from '../../ui/List';
import { Sheet } from '../../ui/Sheet';
import { toast } from '../../ui/Toast';
import { Notes } from '../notes/Notes';
import { Photos } from '../photos/Photos';
import s from './PlanSheet.module.css';

export function mapsUrl(p: Place): string {
  const q = p.address
    ? `${p.name}, ${p.address}`
    : p.lat != null && p.lon != null
      ? `${p.lat},${p.lon}`
      : p.name;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function When({
  label,
  at,
  tz,
  prefs,
}: {
  label: string;
  at: number | null;
  tz: string | null;
  prefs: ReturnType<typeof selectPrefs>;
}) {
  if (at == null || !tz) return null;
  return (
    <div className={s.when}>
      <span className="t-footnote muted">{label}</span>
      <span className={`${s.whenTime} num`}>{formatTime(at, tz, prefs)}</span>
      <span className="t-footnote muted">
        {formatDayShort(localDate(at, tz), prefs)}, {tzAbbr(at, tz)}
      </span>
    </div>
  );
}

export function Confirmation({ code }: { code: string }) {
  return (
    <button
      type="button"
      className={s.confirmation}
      onClick={async () => {
        if (await copyText(code)) {
          void haptics.tick();
          toast('Confirmation copied');
        }
      }}
    >
      <span className="t-footnote muted">Confirmation</span>
      <span className={`${s.code} num`}>{code}</span>
      <Icon name="copy-simple" size={18} className={s.copyIcon} />
    </button>
  );
}

function Fact({ icon, label, children }: { icon?: IconName; label: string; children: ReactNode }) {
  return (
    <div className={s.fact}>
      {icon && <Icon name={icon} size={18} className={s.factIcon} />}
      <span className="t-footnote muted">{label}</span>
      <span className={s.factValue}>{children}</span>
    </div>
  );
}

function detailFacts(item: Item): { label: string; value: string }[] {
  const d = item.details;
  const out: { label: string; value: string }[] = [];
  const add = (label: string, value: unknown) => {
    if (value != null && value !== '') out.push({ label, value: String(value) });
  };
  switch (d.type) {
    case 'flight':
      add('Seat', d.seat);
      add('Cabin', d.cabin);
      add('Fare class', d.fareClass);
      add('Aircraft', d.aircraft);
      if (d.operatingAirline && d.operatingAirline !== d.airline)
        add('Operated by', `${d.operatingAirline} ${d.operatingFlightNumber ?? ''}`.trim());
      break;
    case 'lodging':
      add('Room', d.room);
      add('Guests', d.guests);
      add('Host', d.host);
      add('Program', d.program);
      break;
    case 'car':
      add('Car', d.carClass);
      add('Program', d.program);
      break;
    case 'train':
    case 'bus':
    case 'ferry':
      add('Number', d.number);
      add('Coach', d.coach);
      add('Seat', d.seat);
      add('Class', d.class);
      break;
    case 'cruise':
      add('Ship', d.ship);
      add('Cabin', d.cabin);
      break;
    case 'restaurant':
      add('Party of', d.partySize);
      break;
    case 'activity':
      add('Venue', d.venue);
      add('Tickets', d.tickets);
      break;
    case 'transfer':
      add('Company', d.company);
      break;
    case 'parking':
      add('Facility', d.facility);
      add('Spot', d.spot);
      break;
  }
  return out;
}

function MoveTrip({ item, onDone }: { item: Item; onDone: () => void }) {
  const lists = useStore(selectTripLists);
  const trips = [...lists.current, ...lists.upcoming, ...lists.past];
  return (
    <ListGroup title="Move to trip">
      {trips
        .filter((t) => t.id !== item.tripId)
        .slice(0, 12)
        .map((t) => (
          <ListRow
            key={t.id}
            title={t.title}
            detail={t.startDate ?? undefined}
            onClick={async () => {
              await patch('items', item.id, { tripId: t.id, tripLocked: true });
              await widenTripFor(t.id, item);
              toast(`Moved to ${t.title}`);
              onDone();
              navigate(`/trips/${t.id}`, { replace: true, transition: 'none' });
            }}
          />
        ))}
    </ListGroup>
  );
}

export function PlanSheet({
  itemId,
  open,
  onClose,
}: {
  itemId: string;
  open: boolean;
  onClose: () => void;
}) {
  const item = useStore((st) => st.tables.items.get(itemId));
  const places = useStore(selectPlaceLookup);
  const prefs = useStore(selectPrefs);
  const attachments = useStore((st) => selectAttachmentsByOwner(st).get(itemId)) ?? [];
  const [moving, setMoving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!item) return null;
  const start = places(item.startPlaceId);
  const end = places(item.endPlaceId);
  const facts = detailFacts(item);
  const span = item.type === 'lodging' || item.type === 'car';
  const startLabel =
    item.type === 'lodging'
      ? 'Check in'
      : item.type === 'car'
        ? 'Pick up'
        : item.endAt != null && span
          ? 'Starts'
          : item.endPlaceId
            ? 'Departs'
            : 'When';
  const endLabel =
    item.type === 'lodging'
      ? 'Check out'
      : item.type === 'car'
        ? 'Return'
        : item.endPlaceId
          ? 'Arrives'
          : 'Until';
  const lengthNote =
    item.type === 'lodging'
      ? plural(spanNights(item), 'night')
      : item.type === 'car'
        ? plural(rentalDays(item), 'day')
        : item.startAt != null && item.endAt != null
          ? formatDuration((item.endAt - item.startAt) / 60_000)
          : null;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={item.title}
      actions={
        <Button
          size="sm"
          variant="quiet"
          icon="pencil-simple"
          onClick={() => navigate(`/plans/${item.id}/edit`, { replace: true, overlay: true })}
        >
          Edit
        </Button>
      }
    >
      <div className={s.body}>
        <div className={s.kind}>
          <span className="t-footnote muted">
            {item.provider && item.provider !== item.title
              ? item.provider
              : ITEM_TYPE_LABELS[item.type]}
          </span>
          {item.status === 'cancelled' && <span className={s.cancelled}>Cancelled</span>}
        </div>

        <div className={s.times}>
          <When label={startLabel} at={item.startAt} tz={item.startTz} prefs={prefs} />
          {item.endAt != null && (
            <When label={endLabel} at={item.endAt} tz={item.endTz ?? item.startTz} prefs={prefs} />
          )}
        </div>
        {lengthNote && <p className="t-footnote muted">{lengthNote}</p>}

        {item.confirmation && <Confirmation code={item.confirmation} />}

        <div className={s.links}>
          {start && (
            <a className={s.linkRow} href={mapsUrl(start)} target="_blank" rel="noreferrer">
              <Icon name="map-pin" size={20} />
              <span>
                <span className={s.linkTitle}>{start.name}</span>
                {start.address && <span className="t-footnote muted">{start.address}</span>}
              </span>
              <Icon name="arrow-square-out" size={16} className={s.out} />
            </a>
          )}
          {end && end.id !== start?.id && (
            <a className={s.linkRow} href={mapsUrl(end)} target="_blank" rel="noreferrer">
              <Icon name="map-pin" size={20} />
              <span>
                <span className={s.linkTitle}>{end.name}</span>
                {end.address && <span className="t-footnote muted">{end.address}</span>}
              </span>
              <Icon name="arrow-square-out" size={16} className={s.out} />
            </a>
          )}
          {item.phone && (
            <a className={s.linkRow} href={`tel:${item.phone.replace(/[^\d+]/g, '')}`}>
              <Icon name="phone" size={20} />
              <span className={`${s.linkTitle} num`}>{item.phone}</span>
            </a>
          )}
          {item.url && (
            <a className={s.linkRow} href={item.url} target="_blank" rel="noreferrer">
              <Icon name="globe-simple" size={20} />
              <span className={`${s.linkTitle} truncate`}>
                {item.url.replace(/^https?:\/\//, '')}
              </span>
              <Icon name="arrow-square-out" size={16} className={s.out} />
            </a>
          )}
        </div>

        {facts.length > 0 && (
          <div className={s.facts}>
            {facts.map((f) => (
              <Fact key={f.label} label={f.label}>
                {f.value}
              </Fact>
            ))}
          </div>
        )}

        {item.cost && (item.cost.amount != null || item.cost.points != null) && (
          <div className={s.facts}>
            {item.cost.amount != null && item.cost.currency && (
              <Fact label="Price">{formatMoney(item.cost.amount, item.cost.currency)}</Fact>
            )}
            {item.cost.points != null && (
              <Fact label="Points">
                {formatNumber(item.cost.points)}
                {item.cost.program ? ` ${item.cost.program}` : ''}
              </Fact>
            )}
            {item.cost.points && item.cost.cashEquivalent ? (
              <Fact label="Value">
                {(
                  (item.cost.cashEquivalent - (item.cost.taxesAmount ?? 0)) /
                  item.cost.points
                ).toFixed(2)}{' '}
                cents a point
              </Fact>
            ) : null}
          </div>
        )}

        <section className={s.section}>
          <h3 className="t-subhead">Notes</h3>
          <Notes value={item.notes} onSave={(notes) => void patch('items', item.id, { notes })} />
        </section>

        <section className={s.section}>
          <h3 className="t-subhead">Attachments</h3>
          <Photos ownerType="item" ownerId={item.id} photos={attachments} />
        </section>

        <p className="t-footnote faint">
          {item.sourceId && item.sourceId !== 'src_sample'
            ? 'Imported from an email'
            : 'Added by hand'}
        </p>

        {moving ? (
          <MoveTrip item={item} onDone={onClose} />
        ) : (
          <ListGroup>
            <ListRow
              icon="suitcase-rolling"
              title="Move to another trip"
              onClick={() => setMoving(true)}
            />
            {item.status === 'active' ? (
              <ListRow
                icon="x"
                title="Mark as cancelled"
                onClick={() => void patch('items', item.id, { status: 'cancelled' })}
              />
            ) : (
              <ListRow
                icon="check"
                title="Mark as active"
                onClick={() => void patch('items', item.id, { status: 'active' })}
              />
            )}
            {confirmDelete ? (
              <ListRow
                icon="trash-simple"
                danger
                title="Delete this plan"
                detail="Tap again to confirm."
                onClick={async () => {
                  await remove('items', item.id);
                  toast('Plan deleted');
                  onClose();
                }}
              />
            ) : (
              <ListRow
                icon="trash-simple"
                danger
                title="Delete plan"
                onClick={() => setConfirmDelete(true)}
              />
            )}
          </ListGroup>
        )}
      </div>
    </Sheet>
  );
}
